import {
  type ExportedMessageRepository,
  type GenericThreadHistoryAdapter,
  getExternalStoreMessages,
  type MessageFormatAdapter,
  type ThreadHistoryAdapter,
} from '@assistant-ui/react';

import { createThreadHistoryStore, type StoredHistory } from '#plugin-chat/lib/transport/threadStorage.ts';

/**
 * A {@link ThreadHistoryAdapter} that additionally exposes
 * {@link ThreadHistoryAdapterWithSnapshot.snapshot} — the single
 * persistence writer.
 *
 * assistant-ui's `useExternalHistory` persists incrementally (per message,
 * on an `isRunning` true→false transition), which forces every special
 * state — cancelled interrupts, pending `askUserQuestion` interrupts,
 * telemetry backfills — to be patched with its own write seam, each
 * reconstructing parent links from the flat active branch. `snapshot`
 * replaces all of that: it persists the runtime's complete message tree
 * (every branch, real parent links, the active head) as one atomic document.
 */
export type ThreadHistoryAdapterWithSnapshot = ThreadHistoryAdapter & {
  /**
   * Atomically persist the full repository export, replacing the stored
   * document. Refuses to write (loudly, via `console.error`) while the
   * thread is not hydrated — see the gate inside
   * {@link createThreadHistoryAdapter} — so a failed or skipped history load
   * can never cause an empty in-memory thread to overwrite good persisted
   * data.
   */
  snapshot(repo: ExportedMessageRepository): void;
};
/**
 * The vendored `aiSDKV6FormatAdapter` with its `MessageType` generic
 * erased. Captured in `withFormat` and reused by the snapshot encoder so
 * the explicit-snapshot path encodes byte-identically to what the format
 * adapter would produce (same `getId`/`format`/`encode` — no mirrored
 * format literal, no deep import).
 */
type ErasedFormatAdapter = {
  format: string;
  getId(message: unknown): string;
  encode(item: { parentId: string | null; message: unknown }): Record<string, unknown>;
};

/** A repository message as it appears in an export item / the active branch. */
type RepositoryMessage = ExportedMessageRepository['messages'][number]['message'];

/**
 * Overlay the active branch's trailing messages onto the exported tree, so a
 * snapshot captures the just-finished assistant turn that `export()` drops.
 *
 * `runtime.thread.export()` omits any message still flagged `isOptimistic`, and
 * a streamed assistant message keeps that flag even after its run settles
 * (`isRunning` false) — so export permanently excludes it while
 * `getState().messages` (the active branch) always carries it. The optimistic
 * tail only ever exists on the active branch, so appending the active-branch
 * messages the export omitted reconstructs the complete tree WITHOUT ever
 * resurrecting a stale sibling branch (siblings are past, committed runs and so
 * are already in the export). Parent links for the appended tail follow the
 * active branch, which is linear by construction.
 */
export function withActiveBranchTail(
  exported: ExportedMessageRepository,
  activeMessages: readonly RepositoryMessage[],
): ExportedMessageRepository {
  const exportedIds = new Set(exported.messages.map((item) => item.message.id));
  const tail = activeMessages.flatMap((message, index) =>
    exportedIds.has(message.id) ? [] : [{ message, parentId: activeMessages[index - 1]?.id ?? null }],
  );
  if (tail.length === 0) {
    return exported;
  }
  return {
    headId: activeMessages.at(-1)?.id ?? exported.headId,
    messages: [...exported.messages, ...tail],
  };
}

/**
 * Creates a per-thread {@link ThreadHistoryAdapterWithSnapshot} backed by
 * IndexedDB.
 *
 * **Load** is assistant-ui-native: `useAISDKRuntime` drives it through
 * `withFormat`, calling `load()` once on mount for a thread that already has a
 * server `remoteId`; the runtime re-`import`s the full
 * `ExportedMessageRepository`, so every branch (edits, regenerations) is
 * restored and branch-switching survives a reload.
 *
 * **Write** is exclusively {@link ThreadHistoryAdapterWithSnapshot.snapshot}
 * (driven by `useThreadSnapshotPersistence`): the full repository export,
 * encoded entry-per-inner-message, saved as one atomic document. The
 * `withFormat` `append` is therefore a no-op and `update` is absent —
 * incremental writes would re-create the multi-writer read-modify-write
 * model whose failure amplification (an empty/divergent read becoming the
 * base of the next write) this adapter exists to eliminate.
 *
 * **Hydration gate — confirm once, then trust.** The only data loss to
 * prevent is a never-hydrated thread (a failed or skipped history load leaves
 * the runtime empty) overwriting good persisted data. So `snapshot` confirms
 * hydration ONCE: the first write must be a superset of the ids already on disk
 * — proof the runtime imported them. An empty disk (a new thread) makes that
 * first write a trivial superset, so writes flow immediately; a failed import
 * leaves the first write short of the baseline and it is refused.
 *
 * After that single confirmation the runtime is the source of truth and every
 * write persists as-is — because edits are NOT append-only: a user-message edit
 * replaces the branch point (the old sibling is dropped from the runtime's own
 * tree), so a strict per-write superset check would wrongly refuse it. The
 * empty-doc guard still blocks the degenerate overwrite regardless.
 *
 * Reading the baseline at construction — NOT from `withFormat().load()` — is
 * essential: the vendored `useExternalHistory` skips that load for any thread
 * without a server `remoteId` (every brand-new thread, and any thread until
 * `initialize` assigns one), so a load-derived baseline would never be set and
 * the gate would refuse every snapshot — persisting nothing.
 */
export function createThreadHistoryAdapter(threadId: string): ThreadHistoryAdapterWithSnapshot {
  const store = createThreadHistoryStore(threadId);

  // Read the gate baseline HERE, at construction, unconditionally — NOT in
  // `withFormat().load()`, which the vendored `useExternalHistory` skips for
  // any thread without a server `remoteId` (see the class docstring). The
  // `formatAdapter` is still captured lazily in `withFormat` (the only place
  // assistant-ui hands it to us).
  //
  // `baseline` is the set of entry ids already on disk: `null` until the read
  // resolves (an early snapshot then refuses — unknown state), an empty set for
  // a new thread (the first export is a trivial superset → hydration confirmed
  // at once), or the persisted ids for an existing thread (the first write must
  // cover them to confirm the runtime imported, so a failed/skipped import
  // cannot overwrite disk).
  const state: {
    formatAdapter: ErasedFormatAdapter | null;
    baseline: ReadonlySet<string> | null;
    hydrationConfirmed: boolean;
  } = { formatAdapter: null, baseline: null, hydrationConfirmed: false };

  const loaded = store.load();
  void loaded
    .then(({ entries }) => {
      state.baseline = new Set(entries.map((entry) => entry.id));
    })
    .catch((error) => {
      // Leave `baseline` null on a read error → the gate keeps refusing, so a
      // transient IndexedDB failure can never let an empty export clobber disk.
      console.error(`createThreadHistoryAdapter: failed to read persisted history for "${threadId}":`, error);
    });

  const encodeRepository = (repo: ExportedMessageRepository, formatAdapter: ErasedFormatAdapter): StoredHistory => {
    // One repository message (assistant-ui `ThreadMessage`) carries one or
    // more inner AI SDK `UIMessage`s; entries are keyed by inner ids.
    const innerOf = new Map(
      repo.messages.map((item) => [item.message.id, getExternalStoreMessages<unknown>(item.message)] as const),
    );
    const lastInnerIdOf = new Map(
      [...innerOf]
        .filter(([, inner]) => inner.length > 0)
        .map(([id, inner]) => [id, formatAdapter.getId(inner.at(-1))] as const),
    );

    const entries = repo.messages.flatMap((item) => {
      const inner = innerOf.get(item.message.id) ?? [];
      // Real tree parent from the repository export, resolved to the parent's
      // last inner message; inner messages chain linearly within their
      // repository message (mirrors the vendored `toBatchItems`, but from the
      // tree, not the flat active branch).
      const parentLastInnerId = item.parentId == null ? null : (lastInnerIdOf.get(item.parentId) ?? null);
      return inner.map((message, index) => {
        const parentId = index === 0 ? parentLastInnerId : formatAdapter.getId(inner[index - 1]);
        return {
          id: formatAdapter.getId(message),
          parentId,
          format: formatAdapter.format,
          content: formatAdapter.encode({ parentId, message }),
        };
      });
    });

    const headId = repo.headId == null ? (entries.at(-1)?.id ?? null) : (lastInnerIdOf.get(repo.headId) ?? null);
    return { headId, entries };
  };

  return {
    // Unused by useAISDKRuntime (it requires and uses `withFormat`).
    async load() {
      return { headId: null, messages: [] };
    },
    async append() {
      // No-op: persistence flows through `snapshot`.
    },

    snapshot(repo) {
      const { formatAdapter, baseline } = state;
      if (!formatAdapter) {
        // `withFormat` is invoked by `useExternalHistory` on mount, before
        // any thread change can reach this. Nothing meaningful to encode yet.
        return;
      }
      const doc = encodeRepository(repo, formatAdapter);
      if (doc.entries.length === 0) {
        // Nothing to persist — and never write empty documents: draft threads
        // would otherwise litter storage, and an empty overwrite is exactly
        // the data loss the gate exists to prevent.
        return;
      }
      if (doc.entries.length < repo.messages.length) {
        // A repository message without inner messages cannot be encoded;
        // writing a document with holes would corrupt parent links.
        console.error(
          `createThreadHistoryAdapter: snapshot for "${threadId}" skipped — ` +
            `${repo.messages.length - doc.entries.length} repository message(s) carry no inner message; ` +
            'keeping the previously persisted document',
        );
        return;
      }
      // Hydration gate — confirm ONCE, then trust the runtime. The only loss to
      // prevent is a never-hydrated thread (a failed or skipped history load
      // leaves the runtime empty) overwriting good persisted data. So the FIRST
      // write must be a superset of what was on disk — proof the runtime
      // imported it. After that the runtime is the source of truth: its edits
      // replace rather than only append (a user-message edit drops the old
      // branch), so a strict per-write superset would wrongly refuse them. The
      // empty-doc guard above still blocks the worst case regardless.
      if (!state.hydrationConfirmed) {
        const ids = new Set(doc.entries.map((entry) => entry.id));
        const superset = baseline != null && [...baseline].every((id) => ids.has(id));
        if (!superset) {
          console.error(
            `createThreadHistoryAdapter: snapshot for "${threadId}" refused — ` +
              (baseline == null
                ? 'the persisted document has not been read yet (a write raced construction)'
                : 'the runtime has not hydrated from the persisted document (failed or skipped history load)') +
              '; keeping the previously persisted document rather than overwriting it',
          );
          return;
        }
        state.hydrationConfirmed = true;
      }
      // Fire-and-forget: IndexedDB writes are async and must never throw into
      // the subscribe callback that drives snapshots. A rejection (DB error)
      // is logged; the previously persisted document survives.
      void store.save(doc).catch((error) => {
        console.error(`createThreadHistoryAdapter: failed to persist snapshot for "${threadId}":`, error);
      });
    },

    withFormat<MessageType, StorageFormatType extends Record<string, unknown>>(
      formatAdapter: MessageFormatAdapter<MessageType, StorageFormatType>,
    ): GenericThreadHistoryAdapter<MessageType> {
      // Capture the vendored adapter for the snapshot encoder. The runtime
      // adapter is `aiSDKV6FormatAdapter`, whose `encode`/`getId` operate on
      // the AI SDK `UIMessage` — exactly the shape `getExternalStoreMessages`
      // recovers from an exported repository message — so the cast across the
      // erased `MessageType` generic is sound at this vendored boundary.
      state.formatAdapter = formatAdapter as unknown as ErasedFormatAdapter;

      return {
        async load() {
          // Reuse the constructor read (same disk, and it already establishes
          // the gate baseline). This runs only when the vendored loadHistory
          // calls it — i.e. for a thread with a remoteId — handing the decoded
          // branches to the runtime to import.
          const { headId, entries } = await loaded;
          return {
            headId,
            messages: entries.map((entry) =>
              // assistant-ui's `MessageStorageEntry` uses snake_case
              // `parent_id`; map our camelCase field at this boundary.
              formatAdapter.decode({
                id: entry.id,
                // biome-ignore lint/style/useNamingConvention: external assistant-ui MessageStorageEntry contract
                parent_id: entry.parentId,
                format: entry.format,
                content: entry.content as StorageFormatType,
              }),
            ),
          };
        },

        async append() {
          // No-op: `snapshot` is the single writer. (`update` is
          // intentionally absent — it is optional and the vendored
          // settle-persist skips a missing `update`.)
        },
      };
    },
  };
}
